import { GcpPlacementService, ScheduledFlight, Site } from '../../../../../types';
import { determineIfScopeIsPanoOnly, determineIfScopeIsScan } from '../../helpers/scope';
import { Alert } from '@traceair/tds';
import { useIsMissionModerator } from '../../../../../hooks/useIsMissionModerator';
import { stylesheet } from 'typestyle';
import { useAppStore } from '../../../../../store/appStore';

const css = stylesheet({
  alerts: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 24,
  },
});

interface Props {
  site: Site;
  schedule: ScheduledFlight | undefined;
  gcpPlacementService: GcpPlacementService | undefined;
}

export const FlightUploadAlerts = ({ site, schedule, gcpPlacementService }: Props) => {
  const isModerator = useIsMissionModerator();

  const { pilot } = useAppStore();

  const isUnsupportedScope =
    schedule && !determineIfScopeIsPanoOnly(schedule.scope) && !determineIfScopeIsScan(schedule.scope);

  const scheduleOrGcp = schedule ?? gcpPlacementService;
  const showModeratorAlert = isModerator && !scheduleOrGcp;

  const showNoWorkerSupport = typeof Worker === 'undefined';

  const showHasNoAccess = !site.hasUploadPermission;

  if (!isUnsupportedScope && !showModeratorAlert && !showNoWorkerSupport && !showHasNoAccess) return null;

  return (
    <div className={css.alerts}>
      {showModeratorAlert && (
        <Alert
          type="attention"
          title="You have moderator access"
          description="If you want to upload a scheduled scan or panoramas, please use the 'Scheduled' tab to avoid duplicates in AT."
        />
      )}

      {isUnsupportedScope && (
        <Alert
          type="warning"
          title={`${schedule.scope} uploads are not supported yet`}
          description={`Uploading ${schedule.scope} files is not supported at this time. Please upload the files to Google Drive and share the link with OPS.`}
        />
      )}

      {showNoWorkerSupport && (
        <Alert
          type="warning"
          title="Your browser is not supported"
          description="Client-side validations won't work in your browser. Please use a different browser, or proceed with file uploads at your own risk."
        />
      )}

      {showHasNoAccess && (
        <Alert
          type="warning"
          title="You don't have upload permissions"
          description={`You are not allowed to upload files to this site. Please contact the ${
            pilot ? 'Operations' : 'CS'
          } team if you need access.`}
        />
      )}
    </div>
  );
};
